import mixpanel from 'mixpanel-browser'
import { isMixpanelReady } from './consent'

type EventProps = Record<string, unknown>

let isAuthenticated = false

export const setAnalyticsAuthenticated = (authenticated: boolean) => {
  if (isAuthenticated === authenticated) return
  isAuthenticated = authenticated
  if (authenticated && isMixpanelReady()) {
    try {
      mixpanel.reset()
    } catch {
      // ignore
    }
  }
}

// Normalize to a stable snake_case key: it's the GTM Custom Event trigger name
// and the GA4 event name (GA4 requires snake_case, <=40 chars, [a-z0-9_]).
const toEventName = (event: string) =>
  event
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 40)

// GA4 caps each param value at 100 chars. Drop functions/objects, stringify rest.
const sanitizeProps = (props?: EventProps) => {
  if (!props) return {}
  const out: Record<string, string | number | boolean> = {}
  for (const [k, v] of Object.entries(props)) {
    if (v === undefined || v === null) continue
    if (typeof v === 'string') out[k] = v.slice(0, 100)
    else if (typeof v === 'number' || typeof v === 'boolean') out[k] = v
    else out[k] = String(v).slice(0, 100)
  }
  return out
}

// Model B: push once to the dataLayer. GTM (GTM-T5WQL44Q) owns the fan-out —
// a GA4 Event tag and a consent-gated Meta Pixel tag both trigger off the
// snake_case `event` key. This keeps event logic in one place and lets
// marketing add/change conversion tags without a code deploy.
const pushDataLayer = (event: string, props?: EventProps) => {
  if (typeof window === 'undefined') return
  try {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      // props first so they can never clobber the reserved trigger keys below
      ...sanitizeProps(props),
      event: toEventName(event), // GTM Custom Event trigger, e.g. contact_form_succeeded
      event_label: event, // original human name, e.g. "Contact Form Succeeded"
    })
  } catch {
    // never let analytics break the app
  }
}

export const track = (event: string, props?: EventProps) => {
  if (isAuthenticated) return
  if (isMixpanelReady()) {
    try {
      mixpanel.track(event, props)
    } catch {
      // never let analytics break the app
    }
  }
  // GTM tags are consent-gated (Meta requires ad_storage; GA4 respects Consent
  // Mode natively) — safe to push unconditionally.
  pushDataLayer(event, props)
}

export const trackPageView = (path: string, props?: EventProps) => {
  track('Page Viewed', { path, ...props })
}

export const ANALYTICS_EVENTS = {
  PAGE_VIEWED: 'Page Viewed',

  NAV_LINK_CLICKED: 'Nav Link Clicked',
  NAV_DROPDOWN_OPENED: 'Nav Dropdown Opened',
  NAV_MOBILE_MENU_OPENED: 'Nav Mobile Menu Opened',
  NAV_LOGO_CLICKED: 'Nav Logo Clicked',

  HOME_HERO_CTA_CLICKED: 'Home Hero CTA Clicked',
  HOME_PRODUCT_CARD_CLICKED: 'Home Product Card Clicked',
  HOME_PRODUCT_ENQUIRE_CLICKED: 'Home Product Enquire Clicked',
  HOME_JOB_REFERENCE_CLICKED: 'Home Job Reference Clicked',
  HOME_MISSION_CTA_CLICKED: 'Home Mission CTA Clicked',
  HOME_MID_CTA_CLICKED: 'Home Mid CTA Clicked',
  HOME_INDUSTRY_CARD_CLICKED: 'Home Industry Card Clicked',
  HOME_CROSS_SELL_CLICKED: 'Home Cross Sell Clicked',
  HOME_FLOORING_CARD_CLICKED: 'Home Flooring Card Clicked',
  HOME_EQUIPMENT_CARD_CLICKED: 'Home Equipment Card Clicked',
  HOME_EQUIPMENT_CATALOGUE_CLICKED: 'Home Equipment Catalogue Clicked',

  CONTACT_FORM_STARTED: 'Contact Form Started',
  CONTACT_FORM_FIELD_BLURRED: 'Contact Form Field Blurred',
  CONTACT_FORM_SUBMITTED: 'Contact Form Submitted',
  CONTACT_FORM_SUCCEEDED: 'Contact Form Succeeded',
  CONTACT_FORM_FAILED: 'Contact Form Failed',

  JOB_APPLICATION_VIEWED: 'Job Application Viewed',
  JOB_APPLICATION_STARTED: 'Job Application Started',
  JOB_APPLICATION_SUBMITTED: 'Job Application Submitted',
  JOB_APPLICATION_SUCCEEDED: 'Job Application Succeeded',
  JOB_APPLICATION_FAILED: 'Job Application Failed',
  JOB_APPLY_CTA_CLICKED: 'Job Apply CTA Clicked',

  BROCHURE_OPENED: 'Brochure Opened',

  NEWSLETTER_SUBSCRIBE_SUCCEEDED: 'Newsletter Subscribe Succeeded',
  NEWSLETTER_SUBSCRIBE_FAILED: 'Newsletter Subscribe Failed',

  CONSENT_ACCEPT_ALL_CLICKED: 'Consent Accept All Clicked',
  CONSENT_CUSTOMIZE_OPENED: 'Consent Customize Opened',
  CONSENT_PREFERENCES_SAVED: 'Consent Preferences Saved',

  WHATSAPP_CLICKED: 'WhatsApp Clicked',
  PHONE_CLICKED: 'Phone Clicked',
  SOCIAL_LINK_CLICKED: 'Social Link Clicked',
  FAQ_TOGGLED: 'FAQ Toggled',
  FOOTER_CTA_CLICKED: 'Footer CTA Clicked',
  // Any product card outside the homepage range section — carries `surface` so the
  // listing pages stay distinguishable from each other.
  PRODUCT_CARD_CLICKED: 'Product Card Clicked',
  PRODUCT_DETAIL_CTA_CLICKED: 'Product Detail CTA Clicked',
} as const
